import { type ThemeMode } from "@root/tablescape/state";
import { type TTailwindString } from "classnames";

export const enum BentoBoxLayout {
  A = "a",
  B = "b",
}

/**
 * Shared interface for video source objects used throughout the BentoBox component.
 * Represents a video source with MIME type and URL for browser compatibility.
 */
export type VideoSource = {
  mimeType?: string;
  url?: string;
};

type BentoBoxCardAlignX = "left" | "center";
type BentoBoxCardAlignY = "top" | "center" | "bottom";
type BentoBoxCardBackgroundColor = "brick" | "char" | "sage" | "blue" | "bone";
type BentoBoxCardContentWidthPercentage = "50" | "75" | "100";
type BentoBoxCardTextColor = "white" | "char";
type BentoBoxCardTitleSize =
  | "display"
  | "xxl"
  | "xl"
  | "lg"
  | "md"
  | "sm"
  | "xs";

type BentoBoxCardProps = {
  alignX?: BentoBoxCardAlignX;
  alignY?: BentoBoxCardAlignY;
  backgroundColor?: BentoBoxCardBackgroundColor;
  btnLabel?: string;
  cardUrl?: string;
  className?: TTailwindString;
  contentWidthPercentage: BentoBoxCardContentWidthPercentage;
  descriptionText?: string;
  eyebrowText?: string;
  handleCtaClick?: () => void;
  imageAlt?: string;
  imageUrl?: string;
  loopVideo: boolean;
  mobileImageAlt?: string;
  mobileImageUrl?: string;
  modalContent?: {
    eyebrowText?: string;
    items: {
      bodyText?: string;
      desktopImage?: string;
      mobileImage?: string;
      titleText?: string;
    }[];
    showModal: boolean;
    titleText?: string;
  };
  position: number;
  textColor?: BentoBoxCardTextColor;
  titleSize?: BentoBoxCardTitleSize;
  titleText?: string;
  /**
   * Preferred method for desktop video sources.
   * Supports multiple video formats with MIME types for better browser compatibility.
   * Falls back to videoWebm/videoMp4 if not provided.
   */
  videoDesktop?: Array<{ mime_type: string; url: string }>;
  /**
   * Alt text for desktop video. Used for accessibility (screen readers).
   * Falls back to titleText if not provided.
   */
  videoDesktopAlt?: string;
  /**
   * Preferred method for mobile video sources.
   * Supports multiple video formats with MIME types for better browser compatibility.
   * Falls back to videoDesktop → videoWebm/videoMp4 if not provided.
   */
  videoMobile?: Array<{ mime_type: string; url: string }>;
  /**
   * Alt text for mobile video. Used for accessibility (screen readers).
   * Falls back to titleText if not provided.
   */
  videoMobileAlt?: string;
  /**
   * Legacy method for desktop MP4 video source.
   * Deprecated - use videoDesktop array format instead for better compatibility.
   * Kept for backward compatibility with existing content.
   */
  videoMp4?: string;
  /**
   * Legacy method for desktop WebM video source.
   * Deprecated - use videoDesktop array format instead for better compatibility.
   * Kept for backward compatibility with existing content.
   */
  videoWebm?: string;
};

type BentoBoxSectionSettings = {
  cards: BentoBoxCardProps[];
  layout: BentoBoxLayout;
  subtitleText1?: string;
  subtitleText2?: string;
  text: {
    btnLabel: string;
    closeBtnAlt: string;
  };
  theme: ThemeMode;
  titleText?: string;
};

export type {
  BentoBoxCardAlignX,
  BentoBoxCardAlignY,
  BentoBoxCardBackgroundColor,
  BentoBoxCardContentWidthPercentage,
  BentoBoxCardTextColor,
  BentoBoxCardTitleSize,
  BentoBoxCardProps,
  BentoBoxSectionSettings,
};
